import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Lock, Eye, EyeOff, ArrowLeft, CheckCircle } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { requestPasswordResetOTP, verifyPasswordResetOTPOnly, verifyPasswordResetOTP, clearError } from '../../store/slices/authSlice';
import { addNotification } from '../../store/slices/uiSlice';
import { PhoneInputWithValidation, PhoneValidationResponse } from '../../components/UI/PhoneInputWithValidation';
import Button from '../../components/UI/Button';

interface PasswordFormData {
  new_password: string;
  new_password_confirm: string;
}

const PasswordResetPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error } = useAppSelector((state) => state.auth);
  const [step, setStep] = useState<'phone' | 'otp' | 'password' | 'success'>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('JO');
  const [phoneValidation, setPhoneValidation] = useState<PhoneValidationResponse | null>(null);
  const [otpCode, setOtpCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors },
    watch,
    reset: resetPasswordForm,
  } = useForm<PasswordFormData>();

  const newPassword = watch('new_password');

  // Clear any autofilled values when password step is shown
  useEffect(() => {
    if (step === 'password') {
      // Small delay to ensure browser autofill has completed, then clear if it's the OTP
      const timer = setTimeout(() => {
        const input = document.getElementById('new_password') as HTMLInputElement;
        if (input && input.value === otpCode && otpCode.length === 6) {
          input.value = '';
          resetPasswordForm({ new_password: '', new_password_confirm: '' });
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [step, otpCode, resetPasswordForm]);
  // Helper function to extract user-friendly error message
  const getErrorMessage = (error: any): string => {
    if (typeof error === 'string') {
      return error;
    }
    
    if (error?.message) {
      return typeof error.message === 'string' ? error.message : JSON.stringify(error.message);
    }
    
    if (error?.details) {
      return typeof error.details === 'string' ? error.details : JSON.stringify(error.details);
    }
    
    return 'An error occurred. Please try again.';
  };

  const onPhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(clearError());
    
    // Validate phone before submission
    if (!phoneValidation || !phoneValidation.valid) {
      dispatch(addNotification({
        type: 'error',
        title: 'Invalid Phone Number',
        message: 'Please enter a valid phone number',
      }));
      return;
    }
    
    // Use normalized phone (E.164 format) for submission
    const normalizedPhone = phoneValidation.normalized;
    const detectedCountry = phoneValidation.country;
    
    try {
      const result = await dispatch(requestPasswordResetOTP({ 
        phone: normalizedPhone,
        country_code: detectedCountry 
      }));
      
      if (requestPasswordResetOTP.fulfilled.match(result)) {
        setPhoneNumber(normalizedPhone);
        setCountryCode(detectedCountry);
        setStep('otp');
        dispatch(addNotification({
          type: 'success',
          title: 'OTP Sent',
          message: 'Please check your phone for the verification code',
        }));
      }
    } catch (error) {
      // Error is already handled by the rejected case
    }
  };

  const onOTPSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(clearError());
    
    if (!otpCode || otpCode.length !== 6) {
      dispatch(addNotification({
        type: 'error',
        title: 'Invalid Code',
        message: 'Please enter a valid 6-digit code',
      }));
      return;
    }
    
    // Verify OTP before proceeding to password step
    try {
      const result = await dispatch(verifyPasswordResetOTPOnly({
        phone: phoneNumber, // Already normalized from onPhoneSubmit
        country_code: countryCode,
        otp_code: otpCode,
      }));
      
      if (verifyPasswordResetOTPOnly.fulfilled.match(result)) {
        // OTP verified successfully - proceed to password step
        // Reset the password form to clear any autofilled values
        resetPasswordForm();
        setStep('password');
        dispatch(addNotification({
          type: 'success',
          title: 'Code Verified',
          message: 'Please enter your new password',
        }));
      } else if (verifyPasswordResetOTPOnly.rejected.match(result)) {
        // OTP verification failed
        const errorMessage = result.payload as string || 'Invalid verification code';
        dispatch(addNotification({
          type: 'error',
          title: 'Verification Failed',
          message: errorMessage,
        }));
      }
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        title: 'Verification Failed',
        message: 'An unexpected error occurred. Please try again.',
      }));
    }
  };

  const onPasswordSubmit = async (data: PasswordFormData) => {
    dispatch(clearError());
    
    if (data.new_password !== data.new_password_confirm) {
      dispatch(addNotification({
        type: 'error',
        title: 'Password Mismatch',
        message: 'Passwords do not match',
      }));
      return;
    }
    
    try {
      const result = await dispatch(verifyPasswordResetOTP({
        phone: phoneNumber, // Already normalized from onPhoneSubmit
        country_code: countryCode,
        otp_code: otpCode,
        new_password: data.new_password,
        new_password_confirm: data.new_password_confirm,
      }));
      
      if (verifyPasswordResetOTP.fulfilled.match(result)) {
        // Show success step
        setStep('success');
        
        dispatch(addNotification({
          type: 'success',
          title: 'Password Reset Successful',
          message: 'Your password has been reset. You can now login with your new password.',
        }));
        
        // Redirect to admin login page after a delay
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else if (verifyPasswordResetOTP.rejected.match(result)) {
        // Error is already set in the reducer, but we can also show a notification
        const errorMessage = result.payload as string || 'Failed to reset password';
        
        // Check if it's an expired OTP error
        if (errorMessage.toLowerCase().includes('expired') || 
            errorMessage.toLowerCase().includes('otp has expired') ||
            errorMessage.toLowerCase().includes('expired or not found')) {
          dispatch(addNotification({
            type: 'error',
            title: 'Session Expired',
            message: 'Your verification code has expired. Please request a new code and try again.',
          }));
        } else {
          dispatch(addNotification({
            type: 'error',
            title: 'Password Reset Failed',
            message: errorMessage,
          }));
        }
      }
    } catch (error) {
      // Fallback error handling
      dispatch(addNotification({
        type: 'error',
        title: 'Password Reset Failed',
        message: 'An unexpected error occurred. Please try again.',
      }));
    }
  };

  const handleResendOTP = async () => {
    dispatch(clearError());
    
    if (!phoneNumber) {
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: 'Phone number is required',
      }));
      return;
    }
    
    try {
      // phoneNumber is already normalized from onPhoneSubmit
      const result = await dispatch(requestPasswordResetOTP({ 
        phone: phoneNumber,
        country_code: countryCode 
      }));
      
      if (requestPasswordResetOTP.fulfilled.match(result)) {
        dispatch(addNotification({
          type: 'success',
          title: 'OTP Resent',
          message: 'A new verification code has been sent to your phone',
        }));
      }
    } catch (error) {
      // Error is already handled by the rejected case
    }
  };

  const handleBackToPhone = () => {
    setStep('phone');
    setPhoneNumber('');
    setOtpCode('');
    dispatch(clearError());
  };

  const handleBackToOTP = () => {
    setStep('otp');
    dispatch(clearError());
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden relative">
      {/* Background Graphics */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 right-10 w-96 h-96 bg-gradient-to-br from-[#33FF00]/10 to-[#2BD400]/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 bg-gradient-to-tr from-[#2BD400]/10 to-[#33FF00]/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full space-y-8">
          {/* Back Button */}
          <div className="flex items-center">
            <Link 
              to="/login" 
              className="flex items-center text-gray-300 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to login
            </Link>
          </div>

          {/* Header */}
          <div className="text-center">
            <div className="flex items-center justify-center space-x-3 mb-6">
              <img 
                src="/logo.jpg" 
                alt="Mirsal Logo" 
                className="h-10 w-10 rounded-lg object-cover"
              />
              <span className="text-2xl font-bold text-white">MIRSAL</span>
            </div>
            <h2 className="text-3xl font-bold text-white">Reset Password</h2>
            <p className="mt-2 text-gray-300">
              {step === 'phone' && 'Enter your WhatsApp phone number to receive a verification code'}
              {step === 'otp' && `Enter the code sent to ${phoneNumber}`}
              {step === 'password' && 'Enter your new password'}
              {step === 'success' && 'Your password has been successfully reset!'}
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8">
            {/* Error Message */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-4 mb-6">
                <p className="text-sm text-red-400">
                  {getErrorMessage(error)}
                </p>
              </div>
            )}

            {step === 'phone' ? (
              // Phone Number Form
              <form onSubmit={onPhoneSubmit} className="space-y-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                    Phone Number
                  </label>
                  <PhoneInputWithValidation
                    value={phoneNumber}
                    onChange={(phoneValue, countryCodeValue) => {
                      setPhoneNumber(phoneValue);
                      setCountryCode(countryCodeValue);
                    }}
                    onValidationChange={setPhoneValidation}
                    error={typeof error === 'string' ? error : (error as any)?.message || undefined}
                    required
                    showValidationResult={false}
                    placeholder="Enter phone number"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={!phoneValidation?.valid || loading}
                  className="w-full py-3 bg-gradient-to-r from-[#33FF00] to-[#2BD400] hover:from-[#2BD400] hover:to-[#33FF00] disabled:from-gray-600 disabled:to-gray-700 text-white disabled:text-white rounded-2xl font-medium transition-all duration-300 transform hover:scale-105 disabled:cursor-not-allowed disabled:transform-none"
                  loading={loading}
                >
                  {loading ? 'Sending...' : 'Send Verification Code'}
                </Button>
              </form>
            ) : step === 'otp' ? (
              // OTP Verification Form
              <form onSubmit={onOTPSubmit} className="space-y-6">
                <div>
                  <label htmlFor="otp_code" className="block text-sm font-medium text-gray-300 mb-2">
                    Verification Code
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      id="otp_code"
                      type="tel"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="w-full px-4 py-3 pl-10 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl text-white placeholder-gray-400 focus:outline-none focus:border-[#33FF00]/50 focus:ring-2 focus:ring-[#33FF00]/20 transition-all text-center text-2xl tracking-widest"
                      placeholder="123456"
                      maxLength={6}
                      autoComplete="one-time-code"
                      autoFocus
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-400">Enter the 6-digit code sent to your phone</p>
                  {otpCode && otpCode.length !== 6 && (
                    <p className="mt-1 text-sm text-red-400">Please enter a valid 6-digit code</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || otpCode.length !== 6}
                  className="w-full py-3 bg-gradient-to-r from-[#33FF00] to-[#2BD400] hover:from-[#2BD400] hover:to-[#33FF00] disabled:from-gray-600 disabled:to-gray-700 text-black disabled:text-white rounded-2xl font-medium transition-all duration-300 transform hover:scale-105 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                      Verifying...
                    </div>
                  ) : (
                    'Verify Code'
                  )}
                </button>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    className="flex-1 py-3 bg-white/5 backdrop-blur-sm border border-[#33FF00]/30 text-[#33FF00] hover:bg-[#33FF00]/10 hover:border-[#33FF00]/50 rounded-2xl font-medium transition-all duration-300"
                    onClick={handleResendOTP}
                    disabled={loading}
                  >
                    Resend Code
                  </button>
                  
                  <button
                    type="button"
                    className="flex-1 py-3 bg-white/5 backdrop-blur-sm border border-white/20 text-gray-300 hover:bg-white/10 hover:border-white/30 rounded-2xl font-medium transition-all duration-300"
                    onClick={handleBackToPhone}
                    disabled={loading}
                  >
                    Different Number
                  </button>
                </div>
              </form>
            ) : step === 'password' ? (
              // Password Reset Form
              <form onSubmit={handlePasswordSubmit(onPasswordSubmit)} className="space-y-6" key="password-form" autoComplete="off">
                <div>
                  <label htmlFor="new_password" className="block text-sm font-medium text-gray-300 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      id="new_password"
                      type={showPassword ? 'text' : 'password'}
                      className="w-full px-4 py-3 pl-10 pr-12 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl text-white placeholder-gray-400 focus:outline-none focus:border-[#33FF00]/50 focus:ring-2 focus:ring-[#33FF00]/20 transition-all"
                      placeholder="Enter new password"
                      autoComplete="new-password"
                      data-form-type="other"
                      data-lpignore="true"
                      {...registerPassword('new_password', {
                        required: 'Password is required',
                        minLength: {
                          value: 8,
                          message: 'Password must be at least 8 characters',
                        },
                      })}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {passwordErrors.new_password && (
                    <p className="mt-1 text-sm text-red-400">{passwordErrors.new_password.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="new_password_confirm" className="block text-sm font-medium text-gray-300 mb-2">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      id="new_password_confirm"
                      type={showPasswordConfirm ? 'text' : 'password'}
                      className="w-full px-4 py-3 pl-10 pr-12 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl text-white placeholder-gray-400 focus:outline-none focus:border-[#33FF00]/50 focus:ring-2 focus:ring-[#33FF00]/20 transition-all"
                      placeholder="Confirm new password"
                      autoComplete="new-password"
                      data-form-type="other"
                      data-lpignore="true"
                      {...registerPassword('new_password_confirm', {
                        required: 'Please confirm your password',
                        validate: (value) => value === newPassword || 'Passwords do not match',
                      })}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                      onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                    >
                      {showPasswordConfirm ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {passwordErrors.new_password_confirm && (
                    <p className="mt-1 text-sm text-red-400">{passwordErrors.new_password_confirm.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-gradient-to-r from-[#33FF00] to-[#2BD400] hover:from-[#2BD400] hover:to-[#33FF00] disabled:from-gray-600 disabled:to-gray-700 text-black disabled:text-white rounded-2xl font-medium transition-all duration-300 transform hover:scale-105 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                      Resetting Password...
                    </div>
                  ) : (
                    'Reset Password'
                  )}
                </button>

                <button
                  type="button"
                  className="w-full py-3 bg-white/5 backdrop-blur-sm border border-white/20 text-gray-300 hover:bg-white/10 hover:border-white/30 rounded-2xl font-medium transition-all duration-300"
                  onClick={handleBackToOTP}
                  disabled={loading}
                >
                  Back to Verification
                </button>
              </form>
            ) : step === 'success' ? (
              // Success Step
              <div className="space-y-6 text-center">
                <div className="flex justify-center">
                  <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center">
                    <CheckCircle className="h-12 w-12 text-green-400" />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h3 className="text-2xl font-bold text-white">Password Reset Successful!</h3>
                  <p className="text-gray-300">
                    Your password has been reset successfully. You can now login with your new password.
                  </p>
                </div>
                
                <div className="pt-4">
                  <p className="text-sm text-gray-400 mb-4">
                    Redirecting to login page...
                  </p>
                  <Link
                    to="/login"
                    className="inline-flex items-center justify-center w-full py-3 bg-gradient-to-r from-[#33FF00] to-[#2BD400] hover:from-[#2BD400] hover:to-[#33FF00] text-black rounded-2xl font-medium transition-all duration-300 transform hover:scale-105"
                  >
                    Go to Login
                  </Link>
                </div>
              </div>
            ) : null}
          </div>

          {/* Footer */}
          <div className="text-center text-sm text-gray-400">
            <p>Need help? Contact support@mirsaljo.com</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetPage;

